import { Switch, Typography } from '@mui/material';
import { useStore } from '../../store/store';
import { ColorMode } from '../../store/colorMode';
import { useKeyboard } from 'hooks/useKeyboard';
import { useSearchParams } from 'react-router-dom';
import { QueryKey, VersionMetadata } from 'api/model';
import { useQuery } from '@tanstack/react-query';
import { getVersionMetadata } from 'api/version/requests';
import VersionChip from './VersionChip';
import { SettingRow } from './SettingsView.styles';
import { SectionLabel } from 'components/settingsHub/SectionLabel';

const VERSION = (import.meta.env.VITE_UI_VERSION ?? null) as string | null;
const BRANCH = import.meta.env.VITE_BRANCH as string;
const COMMIT_HASH = import.meta.env.VITE_COMMIT_HASH as string;
const BUILD_DATE = import.meta.env.VITE_BUILD_DATE as string;

const UI_VERSION_METADATA: VersionMetadata = {
  version: VERSION,
  branch: BRANCH,
  commit_hash: COMMIT_HASH,
  build_date: BUILD_DATE,
};

const SettingsView = () => {
  const { colorMode, toggleColorMode } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: apiVersionMetadata, isLoading } = useQuery({
    queryKey: [QueryKey.Version],
    queryFn: () => getVersionMetadata(),
    staleTime: 1000 * 60 * 15, // 15 minutes
  });

  useKeyboard({
    keyDown: (e) => {
      if (e.key === 'Escape' && searchParams.get('config')) {
        setSearchParams({});
      }
    },
  });

  return (
    <div className="flex max-w-2xl flex-col gap-8">
      <section className="flex flex-col gap-3">
        <SectionLabel>Appearance</SectionLabel>
        <SettingRow>
          <div>
            <Typography sx={{ fontWeight: 600 }}>Dark mode</Typography>
            <Typography variant="body2" color="text.secondary">
              Switch between light and dark themes.
            </Typography>
          </div>
          <Switch
            checked={colorMode === ColorMode.Dark}
            onChange={() => toggleColorMode()}
            inputProps={{ 'aria-label': 'Toggle dark mode' }}
          />
        </SettingRow>
      </section>

      <section className="flex flex-col gap-3">
        <SectionLabel>About</SectionLabel>
        <SettingRow>
          <Typography sx={{ fontWeight: 600 }}>API version</Typography>
          {isLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          ) : (
            apiVersionMetadata && <VersionChip type="API" metadata={apiVersionMetadata} />
          )}
        </SettingRow>
        <SettingRow>
          <Typography sx={{ fontWeight: 600 }}>UI version</Typography>
          <VersionChip type="UI" metadata={UI_VERSION_METADATA} />
        </SettingRow>
      </section>
    </div>
  );
};

export default SettingsView;
